import { HomeEvalJobDetailsPage } from '@/app/jobs/HomeEvalJobDetailsPage';
import { JobStatusBadge } from '@/components/badges/JobStatusBadge';
import { useRowClick } from '@/lib/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { homeEvalJobsState } from '@/state/homeEvalJobs.state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { HomeEvalJobDao } from '@rock/db-admin';
import type { HomeEvalJob, HomeId } from '@rock/types';
import { HomeIdSchema } from '@rock/types';
import type { FC } from 'react';
import { useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { toast } from 'sonner';

const log = logger('HomeEvalJobsPage');
const homeEvalJobDao = new HomeEvalJobDao();

export const HomeEvalJobsPage: FC = () => {
    return (
        <Routes>
            <Route index element={<HomeEvalJobsList />} />
            <Route path=":jobId/*" element={<HomeEvalJobDetailsPage />} />
        </Routes>
    );
};

const HomeEvalJobsList: FC = () => {
    const [jobs, jobsLoadable] = useLoadableAtomValue(homeEvalJobsState, []);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [homeIdInput, setHomeIdInput] = useState('');
    const [isExecuting, setIsExecuting] = useState(false);

    const isLoading = jobsLoadable.state === 'loading';
    const hasError = jobsLoadable.state === 'hasError';

    const parsedHomeId = HomeIdSchema.safeParse(homeIdInput.trim());
    const isValidHomeId = parsedHomeId.success;

    const handleOpenDialog = () => {
        setHomeIdInput('');
        setDialogOpen(true);
    };

    const handleExecuteNow = async () => {
        if (!isValidHomeId) return;

        const homeId = parsedHomeId.data as HomeId;
        setIsExecuting(true);
        try {
            const jobId = await homeEvalJobDao.scheduleHomeEvalJobNow(homeId);
            toast.success('Home eval job scheduled', {
                description: `Job ${jobId} was created for home ${homeId}. Evaluation will run shortly.`,
            });
            log.info('Scheduled home eval job from admin', { jobId, homeId });
            setDialogOpen(false);
        } catch (err) {
            log.error('Error scheduling home eval job', { err });
            toast.error('Error scheduling home eval job', {
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
            });
        } finally {
            setIsExecuting(false);
        }
    };

    const executeNowButton = <Button onClick={handleOpenDialog}>Execute Now</Button>;

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Loading jobs...</CardTitle>
                    <CardDescription>Please wait while we fetch the home evaluation jobs.</CardDescription>
                </CardHeader>
            </Card>
        );
    }
    if (hasError) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Error loading jobs</CardTitle>
                    <CardDescription>Unable to load jobs. Please try again later.</CardDescription>
                </CardHeader>
            </Card>
        );
    }

    return (
        <>
            {jobs.length === 0 ? (
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>No jobs found</CardTitle>
                                <CardDescription>No home evaluation jobs have been created yet.</CardDescription>
                            </div>
                            {executeNowButton}
                        </div>
                    </CardHeader>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>Recent Home Evaluation Jobs</CardTitle>
                                <CardDescription>
                                    Showing the {jobs.length} most recent home evaluation jobs.
                                </CardDescription>
                            </div>
                            {executeNowButton}
                        </div>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Job ID</TableHead>
                                    <TableHead>Home ID</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Attempts</TableHead>
                                    <TableHead>Run Date</TableHead>
                                    <TableHead>Evaluated/Applied</TableHead>
                                    <TableHead>Error</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {jobs.map((job) => (
                                    <HomeEvalJobRow key={job.id} job={job} />
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Execute Home Eval Job</DialogTitle>
                        <DialogDescription>Enter the Home ID to run an evaluation job for right now.</DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Input
                            placeholder="Home ID"
                            value={homeIdInput}
                            onChange={(e) => setHomeIdInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && isValidHomeId && !isExecuting) {
                                    void handleExecuteNow();
                                }
                            }}
                            disabled={isExecuting}
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isExecuting}>
                            Cancel
                        </Button>
                        <Button onClick={handleExecuteNow} disabled={!isValidHomeId || isExecuting}>
                            {isExecuting ? 'Scheduling…' : 'Execute Now'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

const HomeEvalJobRow: FC<{ job: HomeEvalJob }> = ({ job }) => {
    const onRowClick = useRowClick();
    return (
        <TableRow className="cursor-pointer" onClick={onRowClick(job.id)}>
            <TableCell className="font-mono text-xs">{job.id}</TableCell>
            <TableCell className="font-mono text-xs">{job.homeId}</TableCell>
            <TableCell>
                <JobStatusBadge status={job.jobStatus} />
            </TableCell>
            <TableCell>{job.attempts}</TableCell>
            <TableCell>{job.startedAt ? new Date(job.startedAt).toLocaleString() : '-'}</TableCell>
            <TableCell>{job.summary ? `${job.summary.appliedCount}/${job.summary.evaluatedCount}` : '-'}</TableCell>
            <TableCell>
                {job.errorMessage ? (
                    <span className="text-destructive text-sm max-w-md truncate block" title={job.errorMessage}>
                        {job.errorMessage}
                    </span>
                ) : (
                    <span className="text-muted-foreground">-</span>
                )}
            </TableCell>
        </TableRow>
    );
};
